import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { Loader2, Send, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { cn } from '@caruso/ui';
import whatsappService, { WhatsAppStatus } from '@/services/whatsapp.service';

export function WhatsAppTest() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [number, setNumber] = useState('');
  const [text, setText] = useState('');
  const [checkResult, setCheckResult] = useState<{ hasWhatsApp: boolean } | null>(null);
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('text');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaCaption, setMediaCaption] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');

  const checkStatus = async () => {
    try {
      const statusData = await whatsappService.getStatus();
      setStatus(statusData);
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  const handleCheckNumber = async () => {
    if (!number) {
      toast({
        title: 'Erro',
        description: 'Digite um número de telefone',
        variant: 'destructive',
      });
      return;
    }

    try {
      setChecking(true);
      setCheckResult(null);
      const result = await whatsappService.checkNumber(number);
      setCheckResult({ hasWhatsApp: result.hasWhatsApp });

      toast({
        title: result.hasWhatsApp ? 'Número válido' : 'Número inválido',
        description: result.hasWhatsApp
          ? 'Este número possui WhatsApp'
          : 'Este número não possui WhatsApp',
        variant: result.hasWhatsApp ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Erro ao verificar número:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível verificar o número',
        variant: 'destructive',
      });
    } finally {
      setChecking(false);
    }
  };

  const handleSendText = async () => {
    if (!number || !text) {
      toast({
        title: 'Erro',
        description: 'Preencha o número e a mensagem',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await whatsappService.sendText({ number, text });

      if (response.success) {
        toast({
          title: 'Sucesso!',
          description: 'Mensagem enviada com sucesso',
        });
        setText('');
      } else {
        toast({
          title: 'Erro',
          description: response.error || 'Erro ao enviar mensagem',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a mensagem',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMedia = async () => {
    if (!number || !mediaUrl) {
      toast({
        title: 'Erro',
        description: 'Preencha o número e a URL da mídia',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await whatsappService.sendMedia({
        number,
        mediaType: 'image',
        mediaUrl,
        caption: mediaCaption,
      });

      if (response.success) {
        toast({
          title: 'Sucesso!',
          description: 'Mídia enviada com sucesso',
        });
        setMediaUrl('');
        setMediaCaption('');
      } else {
        toast({
          title: 'Erro',
          description: response.error || 'Erro ao enviar mídia',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao enviar mídia:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a mídia',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendContact = async () => {
    if (!number || !contactName || !contactNumber) {
      toast({
        title: 'Erro',
        description: 'Preencha todos os campos obrigatórios',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await whatsappService.sendContact({
        number,
        contactName,
        contactNumber,
      });

      if (response.success) {
        toast({
          title: 'Sucesso!',
          description: 'Contato enviado com sucesso',
        });
        setContactName('');
        setContactNumber('');
      } else {
        toast({
          title: 'Erro',
          description: response.error || 'Erro ao enviar contato',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao enviar contato:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar o contato',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendLocation = async () => {
    if (!number || !latitude || !longitude) {
      toast({
        title: 'Erro',
        description: 'Preencha número, latitude e longitude',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await whatsappService.sendLocation({
        number,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        name: locationName,
        address: locationAddress,
      });

      if (response.success) {
        toast({
          title: 'Sucesso!',
          description: 'Localização enviada com sucesso',
        });
        setLatitude('');
        setLongitude('');
        setLocationName('');
        setLocationAddress('');
      } else {
        toast({
          title: 'Erro',
          description: response.error || 'Erro ao enviar localização',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao enviar localização:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a localização',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const isConnected = status?.connection?.connected;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Teste de Envio</h1>
          <p className="text-brand-ivory/60 mt-1">
            Teste diferentes tipos de mensagens pelo WhatsApp
          </p>
        </div>
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border',
          isConnected
            ? 'bg-green-900/20 border-green-500/30 text-green-400'
            : 'bg-red-900/20 border-red-500/30 text-red-400'
        )}>
          {isConnected ? (
            <>
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Conectado</span>
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Desconectado</span>
            </>
          )}
        </div>
      </div>

      {!isConnected && (
        <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
            <p className="text-sm text-yellow-400">
              WhatsApp não está conectado. Vá para a página de configuração para conectar.
            </p>
          </div>
        </div>
      )}

      {/* Verificar Número */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-serif text-brand-gold">Verificar Número</h3>
            <p className="text-sm text-brand-ivory/60">
              Verifique se um número possui WhatsApp
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: 5511999999999"
                value={number}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumber(e.target.value)}
                disabled={!isConnected}
                className="flex-1 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
              <Button
                onClick={handleCheckNumber}
                disabled={!isConnected || checking}
                variant="outline"
              >
                {checking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Verificar'
                )}
              </Button>
            </div>

            {checkResult !== null && (
              <div className={cn(
                'p-3 rounded-lg flex items-center gap-2 border',
                checkResult.hasWhatsApp
                  ? 'bg-green-900/20 border-green-500/30 text-green-400'
                  : 'bg-red-900/20 border-red-500/30 text-red-400'
              )}>
                {checkResult.hasWhatsApp ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Este número possui WhatsApp e pode receber mensagens</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">Este número não possui WhatsApp</span>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Envio de Mensagens */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-serif text-brand-gold">Enviar Mensagem</h3>
            <p className="text-sm text-brand-ivory/60">
              Teste diferentes tipos de mensagens
            </p>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-brand-gold/10">
              {[
                { id: 'text', label: 'Texto' },
                { id: 'media', label: 'Mídia' },
                { id: 'contact', label: 'Contato' },
                { id: 'location', label: 'Localização' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'border-b-2 border-brand-gold text-brand-gold'
                      : 'text-brand-ivory/60 hover:text-brand-ivory'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="pt-4">
              {activeTab === 'text' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Mensagem</label>
                    <textarea
                      className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                      placeholder="Digite sua mensagem..."
                      value={text}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                      disabled={!isConnected}
                      rows={4}
                    />
                  </div>
                  <Button
                    onClick={handleSendText}
                    disabled={!isConnected || loading}
                    variant="premium"
                    className="w-full"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Enviar Mensagem de Texto
                  </Button>
                </div>
              )}

              {activeTab === 'media' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">URL da Mídia</label>
                    <Input
                      placeholder="https://exemplo.com/imagem.jpg"
                      value={mediaUrl}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMediaUrl(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Legenda (opcional)</label>
                    <Input
                      placeholder="Legenda da imagem"
                      value={mediaCaption}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMediaCaption(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <Button
                    onClick={handleSendMedia}
                    disabled={!isConnected || loading}
                    variant="premium"
                    className="w-full"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Enviar Mídia
                  </Button>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Nome do Contato</label>
                    <Input
                      placeholder="João Silva"
                      value={contactName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactName(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Número do Contato</label>
                    <Input
                      placeholder="5511999999999"
                      value={contactNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactNumber(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <Button
                    onClick={handleSendContact}
                    disabled={!isConnected || loading}
                    variant="premium"
                    className="w-full"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Enviar Contato
                  </Button>
                </div>
              )}

              {activeTab === 'location' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Latitude</label>
                      <Input
                        placeholder="-23.550520"
                        value={latitude}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLatitude(e.target.value)}
                        disabled={!isConnected}
                        className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Longitude</label>
                      <Input
                        placeholder="-46.633309"
                        value={longitude}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLongitude(e.target.value)}
                        disabled={!isConnected}
                        className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Nome do Local (opcional)</label>
                    <Input
                      placeholder="Escritório Central"
                      value={locationName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationName(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Endereço (opcional)</label>
                    <Input
                      placeholder="Av. Paulista, 1000"
                      value={locationAddress}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationAddress(e.target.value)}
                      disabled={!isConnected}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>
                  <Button
                    onClick={handleSendLocation}
                    disabled={!isConnected || loading}
                    variant="premium"
                    className="w-full"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Enviar Localização
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
